import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, RefreshCw, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogBody,
} from "@/components/ui/responsive-dialog";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { todayISO } from "@/lib/format";
import {
  VIEWS,
  KINDS,
  STATUSES,
  parseList,
  filterEvents,
  filterMachines,
  toISO,
  fromISO,
  shiftAnchor,
  indexByDate,
} from "@/lib/calendar";
import api from "@/lib/api";
import { CalendarToolbar, Legend } from "@/components/calendar/CalendarToolbar";
import { MonthView, DayDetails, selectedDayLabel } from "@/components/calendar/MonthView";
import { AgendaView } from "@/components/calendar/AgendaView";
import { YearView } from "@/components/calendar/YearView";
import { MachineTimelineView } from "@/components/calendar/MachineTimelineView";
import { DeadlineActionDialog } from "@/components/calendar/DeadlineActionDialog";
import { CalendarActionsProvider } from "@/components/calendar/EventActions";
import { RegisterControlDialog } from "@/components/machine/RegisterControlDialog";

const EMPTY = { events: [], machines: [], customers: [], today: todayISO(), summary: null };

export default function Kalender() {
  const [params, setParams] = useSearchParams();
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const [dialog, setDialog] = useState({ open: false, mode: "plan", event: null });
  const [controlEvent, setControlEvent] = useState(null);
  const [daySheetOpen, setDaySheetOpen] = useState(false);

  // ---------- URL state ----------
  const setParam = useCallback(
    (patch) => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          Object.entries(patch).forEach(([k, v]) => {
            if (v === null || v === undefined || v === "") p.delete(k);
            else p.set(k, String(v));
          });
          return p;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  const rawView = params.get("view");
  const view = VIEWS.includes(rawView) ? rawView : isDesktop ? "month" : "list";
  const anchorISO = params.get("d") || data.today;
  const anchor = useMemo(() => {
    const d = fromISO(anchorISO);
    return isNaN(d.getTime()) ? fromISO(data.today) : d;
  }, [anchorISO, data.today]);
  const selectedDay = params.get("day") || "";
  const kinds = useMemo(() => parseList(params.get("kind"), KINDS), [params]);
  const statuses = useMemo(() => parseList(params.get("status"), STATUSES), [params]);
  const customer = params.get("customer") || "";
  const q = params.get("q") || "";
  const group = params.get("group") === "customer" ? "customer" : "date";

  const activeFilters =
    (kinds.length < KINDS.length ? 1 : 0) + (statuses.length < STATUSES.length ? 1 : 0) + (customer ? 1 : 0) + (q ? 1 : 0);

  // ---------- Data ----------
  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(false);
    try {
      const r = await api.get("/dashboard/calendar");
      setData({ ...EMPTY, ...r.data });
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const events = useMemo(() => filterEvents(data.events, { kinds, statuses, customer, q }), [data.events, kinds, statuses, customer, q]);
  const machines = useMemo(() => filterMachines(data.machines, { customer, q }), [data.machines, customer, q]);
  const summary = useMemo(
    () => ({
      total: events.length,
      overdue: events.filter((e) => e.status === "overdue").length,
      upcoming: events.filter((e) => e.status === "upcoming").length,
      ok: events.filter((e) => e.status === "ok").length,
      planned: events.filter((e) => e.planned_date).length,
    }),
    [events]
  );

  // ---------- Handlers ----------
  const selectDay = (iso) => {
    setParam({ day: iso });
    if (!isDesktop) setDaySheetOpen(true);
  };
  const goToMonth = (dateObj, dayISO) => {
    setParam({ view: "month", d: toISO(dateObj), day: dayISO || null });
    if (dayISO && !isDesktop) setDaySheetOpen(true);
  };
  const openPlan = (e) => setDialog({ open: true, mode: "plan", event: e });
  const openMove = (e) => setDialog({ open: true, mode: "move", event: e });
  const unplan = async (e) => {
    try {
      await api.patch(`/machines/${e.machine_id}/deadlines`, { kind: e.kind, action: "unplan" });
      toast.success(`Planlagt dato fjerna · ${e.label}`);
      load(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikkje fjerne planen.");
    }
  };
  const resetFilters = () => setParam({ kind: null, status: null, customer: null, q: null });

  const viewProps = { events, today: data.today, onPlan: openPlan, onMove: openMove, onUnplan: unplan };
  const calendarActions = useMemo(() => ({ onControl: (e) => setControlEvent(e) }), []);
  // RegisterControlDialog expects a machine-ish object: {id, label, control_interval_months, status.next_control_date}
  const controlMachine = controlEvent
    ? { id: controlEvent.machine_id, label: controlEvent.label, control_interval_months: controlEvent.interval_months, status: { next_control_date: controlEvent.date } }
    : null;
  const dayCount = useMemo(() => {
    if (!selectedDay) return 0;
    const idx = indexByDate(events);
    return (idx.due.get(selectedDay) || []).length + (idx.planned.get(selectedDay) || []).length;
  }, [events, selectedDay]);

  // ---------- Render ----------
  return (
    <CalendarActionsProvider value={calendarActions}>
    <div className="space-y-4" data-testid="calendar-view">
      <CalendarToolbar
        view={view}
        onView={(v) => setParam({ view: v })}
        anchor={anchor}
        onPrev={() => setParam({ d: toISO(shiftAnchor(anchor, view, -1)) })}
        onNext={() => setParam({ d: toISO(shiftAnchor(anchor, view, 1)) })}
        onToday={() => setParam({ d: null, day: view === "month" ? data.today : null })}
        kinds={kinds}
        onKinds={(v) => setParam({ kind: v.length === KINDS.length ? null : v.join(",") })}
        statuses={statuses}
        onStatuses={(v) => setParam({ status: v.length === STATUSES.length ? null : v.join(",") })}
        customer={customer}
        onCustomer={(v) => setParam({ customer: v || null })}
        q={q}
        onQ={(v) => setParam({ q: v || null })}
        customers={data.customers}
        group={group}
        onGroup={(g) => setParam({ group: g === "date" ? null : g })}
        activeFilters={activeFilters}
        onReset={resetFilters}
        summary={summary}
        onRefresh={() => load(true)}
        refreshing={refreshing}
      />

      {error ? (
        <Card className="border-dashed shadow-sm">
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <span className="status-overdue flex h-12 w-12 items-center justify-center rounded-full border"><AlertTriangle className="h-5 w-5" /></span>
            <div className="font-heading font-semibold">Kunne ikkje laste kalenderen</div>
            <p className="max-w-sm text-sm text-muted-foreground">Sjekk nettet og prøv igjen.</p>
            <Button onClick={() => load()} data-testid="calendar-retry"><RefreshCw /> Prøv igjen</Button>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="space-y-3" data-testid="calendar-loading">
          <Skeleton className="h-[420px] w-full rounded-2xl" />
          <Skeleton className="h-16 w-full rounded-2xl" />
        </div>
      ) : (
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={view}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.16, ease: "easeOut" }}
          >
            {view === "month" && (
              <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_380px]">
                <MonthView
                  anchor={anchor}
                  events={events}
                  today={data.today}
                  selected={selectedDay}
                  onSelectDay={selectDay}
                  onOpenEvent={(e, iso) => selectDay(iso)}
                />
                <Card className="hidden shadow-sm lg:block lg:self-start lg:sticky lg:top-20" data-testid="calendar-day-panel">
                  <CardHeader className="pb-3">
                    <CardTitle className="font-heading text-base font-bold" data-testid="calendar-selected-title">
                      {selectedDayLabel(selectedDay)}
                    </CardTitle>
                    {!selectedDay && <p className="text-xs text-muted-foreground">Klikk på ein dag i kalenderen.</p>}
                  </CardHeader>
                  <CardContent className="app-scroll max-h-[calc(100vh-220px)] overflow-y-auto pt-0">
                    {selectedDay ? (
                      <DayDetails dateISO={selectedDay} {...viewProps} />
                    ) : (
                      <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-muted-foreground">
                        <CalendarDays className="h-8 w-8 opacity-40" />
                        Ingen dag vald
                      </div>
                    )}
                    <Legend className="mt-5 border-t pt-4" />
                  </CardContent>
                </Card>
              </div>
            )}

            {view === "list" && (
              <AgendaView {...viewProps} group={group} onFilterCustomer={(id) => setParam({ customer: id || null, group: null })} />
            )}

            {view === "year" && (
              <YearView
                year={anchor.getFullYear()}
                events={events}
                today={data.today}
                onPickMonth={(m) => goToMonth(new Date(anchor.getFullYear(), m, 1))}
                onPickDay={(iso) => goToMonth(fromISO(iso), iso)}
              />
            )}

            {view === "machines" && (
              <MachineTimelineView year={anchor.getFullYear()} machines={machines} {...viewProps} />
            )}
          </motion.div>
        </AnimatePresence>
      )}

      {/* Mobile/tablet: day details as bottom drawer (snap 55% / 94%) or dialog */}
      <ResponsiveDialog open={daySheetOpen && !isDesktop} onOpenChange={setDaySheetOpen} snapPoints={[0.55, 0.94]}>
        <ResponsiveDialogContent className="flex max-h-[88vh] flex-col gap-0 p-0 sm:max-w-lg" data-testid="calendar-day-sheet">
          <ResponsiveDialogHeader className="md:border-b md:px-6 md:pb-4 md:pt-6">
            <ResponsiveDialogTitle className="font-heading" data-testid="calendar-selected-title-mobile">{selectedDayLabel(selectedDay)}</ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {dayCount === 0 ? "Ingen fristar denne dagen." : `${dayCount} ${dayCount === 1 ? "frist" : "fristar"} · trykk på ein for handlingar`}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>
          <ResponsiveDialogBody className="pb-[calc(var(--safe-bottom)+20px)] md:min-h-0 md:flex-1 md:overflow-y-auto md:px-6 md:py-4">
            {selectedDay && <DayDetails dateISO={selectedDay} {...viewProps} />}
          </ResponsiveDialogBody>
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      <DeadlineActionDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
        event={dialog.event}
        mode={dialog.mode}
        today={data.today}
        onDone={() => load(true)}
      />
      <RegisterControlDialog
        open={!!controlEvent}
        onOpenChange={(o) => !o && setControlEvent(null)}
        machine={controlMachine}
        onDone={() => load(true)}
      />
    </div>
    </CalendarActionsProvider>
  );
}
